import React, { useContext, useEffect, useRef, useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import { ReactComponent as Storage } from '../../Resources/Images/LiveView/Storage.svg'
import { ReactComponent as HagerStorage } from '../../Resources/Images/LiveView/HagerStorage.svg'
import LiveViewItem from './LiveViewItem'
import LiveViewSubItem from './LiveViewSubItem'
import { calculateItemAngle, calculateSubItemAngle, clamp } from 'src/Libs/live-view/utils/calculations'
import { UnitTypes } from 'src/Libs/types'
import { Variant } from '@mui/material/styles/createTypography'
import { useMediaQuery, useTheme, Box } from '@mui/material'
import { WallboxHardwareModel } from 'src/Libs/wallboxes/models'
import { Breakpoints } from 'src/Configuration/breakpoints'
import { LiveDataModel, PowermeterHardwareModel } from 'src/State/Storage/StorageModel'
import { InstallationOnlineStatusContext } from 'src/Libs/installations/context/installation-online-status-context'
import { DASHBOARD_LIVE_VIEW_ID } from 'src/Libs/qa/ids'
import { useLiveValueItems } from 'src/Hooks/live-values/use-live-value-items'
import { LiveViewItemSide } from 'src/Libs/live-view'
import { isFlowTheme } from 'src/Libs/utils'
import { stringWithSIPrefix } from 'src/Libs/system-data-handler/utils/formatNumberWithSIPrefix'
import { ENERGY_FLOW_INDICATOR_HEIGHT, ENERGY_FLOW_INDICATOR_SPACING } from './liveViewConstants'

export { ENERGY_FLOW_INDICATOR_HEIGHT, ENERGY_FLOW_INDICATOR_SPACING, ICON_WITH_LABEL_HEIGHT_MOBILE } from './liveViewConstants'

/*
 * the current design uses 5 items view on the mobile while the first one item is empty
 * https://www.figma.com/file/a7EUSoJZdWEfC3wYDJNqyj/E3DC-2022?node-id=4033-190301&t=jtU84rEws93YRbci-0
 */
// const FIRST_LEVEL_4_ITEM_ENERGY_FLOW_INDICATOR_LENGTHS_MOBILE = [3, 8, 8, 3]
const FIRST_LEVEL_5_ITEM_ENERGY_FLOW_INDICATOR_LENGTHS_MOBILE = [3, 8, 11, 8, 3]
const SECOND_LEVEL_2_ITEM_ENERGY_FLOW_INDICATOR_LENGTHS_MOBILE = [3, 3]
const SECOND_LEVEL_3_ITEM_ENERGY_FLOW_INDICATOR_LENGTHS_MOBILE = [5, 3, 5]

const FIRST_LEVEL_4_ITEM_ENERGY_FLOW_INDICATOR_LENGTHS = [5, 8, 8, 5]
const FIRST_LEVEL_5_ITEM_ENERGY_FLOW_INDICATOR_LENGTHS = [5, 4, 8, 4, 5]
const SECOND_LEVEL_2_ITEM_ENERGY_FLOW_INDICATOR_LENGTHS = [3, 6]
const SECOND_LEVEL_3_ITEM_ENERGY_FLOW_INDICATOR_LENGTHS = [3, 3, 6]

const SECOND_LEVEL_2_ITEM_ENERGY_FLOW_INDICATOR_LENGTHS_DOWN_MD = [3, 5]
const SECOND_LEVEL_3_ITEM_ENERGY_FLOW_INDICATOR_LENGTHS_DOWN_MD = [3, 3, 5]
const FIRST_LEVEL_4_ITEMS = 4
const FIRST_LEVEL_FIRST_ICON_REPLACEMENT = 1

export const PRODUCT_ICON_HEIGHT = 92
export const PRODUCT_ICON_HEIGHT_MOBILE = 46
export const PRODUCT_ICON_WIDTH = 189
export const ICON_WITH_LABEL_HEIGHT = 112
export const LOWER_PRODUCT_ICON_HEIGHT = 25
export const LOWER_PRODUCT_ICON_HEIGHT_MOBILE = 12

export interface Props {
  pvHomePower: number
  homeBatteryPower: number
  trackerHomePower: number
  batteryHomePower: number
  homeWallboxPower: number
  homeConsumerPower: number
  consumption: number
  networkHomePower: number
  homeNetworkPower: number
  wallboxes?: WallboxHardwareModel[]
  consumers?: PowermeterHardwareModel[]
  producers?: PowermeterHardwareModel[]
  isFarm: boolean
  liveData?: LiveDataModel
  offlineIcon?: React.ReactNode
  shallowLiveView?: boolean
  isDynamicPowerPriceEnabled?: boolean
  isBlockingTimeActive?: boolean
  showDynamicPrices?: boolean
  storageId: string | undefined
}

interface StyleProps {
  angle?: number
  rotateIcon?: number
  rotateItem?: number
  active?: boolean
  itemsWrapperHeight?: number
}

/* calculate the height in pixels of the liveviewitem dots. Used to calculate the rotation of an LiveViewItem */
const getEnergyFlowIndicatorHeight = (energyFlowIndicatorsCount: number): number => {
  return energyFlowIndicatorsCount * ENERGY_FLOW_INDICATOR_HEIGHT + (energyFlowIndicatorsCount - 1) * ENERGY_FLOW_INDICATOR_SPACING
}

export const getEnergyFlowIndicatorAmountSubLiveItem = (index: number, mobile: boolean, downMd: boolean, itemsAmount: number): number => {
  if (mobile) {
    if (itemsAmount === 2) {
      return SECOND_LEVEL_2_ITEM_ENERGY_FLOW_INDICATOR_LENGTHS_MOBILE[index]
    }
    return SECOND_LEVEL_3_ITEM_ENERGY_FLOW_INDICATOR_LENGTHS_MOBILE[index]
  }
  if (itemsAmount === 2) {
    if (downMd) return SECOND_LEVEL_2_ITEM_ENERGY_FLOW_INDICATOR_LENGTHS_DOWN_MD[index]
    return SECOND_LEVEL_2_ITEM_ENERGY_FLOW_INDICATOR_LENGTHS[index]
  }
  if (downMd) return SECOND_LEVEL_3_ITEM_ENERGY_FLOW_INDICATOR_LENGTHS_DOWN_MD[index]
  return SECOND_LEVEL_3_ITEM_ENERGY_FLOW_INDICATOR_LENGTHS[index]
}

export const getEnergyFlowIndicatorAmountLiveItem = (index: number, mobile: boolean, itemsAmount: number): number => {
  if (mobile) {
    return FIRST_LEVEL_5_ITEM_ENERGY_FLOW_INDICATOR_LENGTHS_MOBILE[index]
  }
  if (itemsAmount === 4) return FIRST_LEVEL_4_ITEM_ENERGY_FLOW_INDICATOR_LENGTHS[index]
  return FIRST_LEVEL_5_ITEM_ENERGY_FLOW_INDICATOR_LENGTHS[index]
}

export const liveDataValueFormatter = (value: number | undefined): string => {
  if (value === undefined) {
    return ''
  }

  return stringWithSIPrefix(value, UnitTypes.watt)
}

const useStyles = makeStyles<StyleProps>()((theme, props) => {
  return {
    middleWrapper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-end',
      height: '100%',
    },
    productIcon: {
      width: PRODUCT_ICON_WIDTH,
      marginBottom: theme.spacing(-1),
    },
    itemsWrapper: {
      position: 'relative',
      width: '100%',
      height: props.itemsWrapperHeight,
    },
    farmProduction: {
      display: 'flex',
      overflow: 'hidden',
    },
    lowerStorage: {
      position: 'relative',
      top: PRODUCT_ICON_HEIGHT - LOWER_PRODUCT_ICON_HEIGHT,
      width: PRODUCT_ICON_WIDTH - 12,
      overflow: 'hidden',

      [theme.breakpoints.down('sm')]: {
        top: PRODUCT_ICON_HEIGHT - LOWER_PRODUCT_ICON_HEIGHT_MOBILE,
      },
    },
    leftStorage: {
      left: theme.spacing(2),
    },
    rightStorage: {
      right: theme.spacing(2),
      display: 'flex',
    },
    middleStorage: {
      translate: '0 10%',
      zIndex: 1,
    },
  }
})

/**
 * LiveView displays installation devices with their current values.
 *
 * The displayed LiveViewItem can show a value of a device, multiple devices or have sub-LiveViewItems itself.
 * For the MVP there are allowed 4 to 5 LiveViewItems on the first level and max two sub-LiveViewItems on the consumption item.
 * This component was implemented before the MVP-requirements were set and therefore is built dynamically with the possibility of adding
 * subitems to each item and updating the angle of the items according to the amount of the items.
 */
const LiveView: React.FC<Props> = ({
  pvHomePower,
  homeBatteryPower,
  trackerHomePower,
  batteryHomePower,
  homeWallboxPower,
  consumption,
  homeConsumerPower,
  networkHomePower,
  homeNetworkPower,
  wallboxes,
  consumers,
  producers,
  isFarm,
  liveData,
  offlineIcon,
  storageId,
  shallowLiveView = false,
  isDynamicPowerPriceEnabled = false,
  isBlockingTimeActive = false,
  showDynamicPrices = false,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  /* max angle of an liveView item */
  const [maxAngle, setMaxAngle] = useState<number>(0)
  /* size used by the translate transformation on the live view item */
  const [translationY, setTranslationY] = useState<number>(120)

  const theme = useTheme()
  const downMobile = useMediaQuery(theme.breakpoints.down(Breakpoints.Mobile))
  const downMd = useMediaQuery(theme.breakpoints.down('md'))
  const wallboxCount = wallboxes?.length ?? 0
  const consumerCount = consumers?.length ?? 0
  const producerCount = producers?.length ?? 0
  const hasFiveItems = shallowLiveView || (!shallowLiveView && Boolean(producerCount))

  const hasAllSecondLevelItems = consumerCount > 0 && wallboxCount > 0

  const liveViewHasSecondRow = wallboxCount > 0 || consumerCount > 0
  const { offlineClientStorageIds } = useContext(InstallationOnlineStatusContext)

  const items = useLiveValueItems({
    batteryHomePower,
    homeBatteryPower,
    pvHomePower,
    networkHomePower,
    homeNetworkPower,
    consumption,
    homeConsumerPower,
    homeWallboxPower,
    consumers,
    liveData,
    offlineClientStorageIds,
    offlineIcon,
    producers,
    trackerHomePower,
    shallowLiveView,
    wallboxes,
    isBlockingTimeActive,
    isDynamicPowerPriceEnabled,
    showDynamicPrices,
    storageId,
  })

  /**
   * The mobile view of the live view has always one level and five items.
   * The desktop view of the live view can have one or two levels and four or five items.
   */
  const firstLevelHeight = getEnergyFlowIndicatorHeight(FIRST_LEVEL_5_ITEM_ENERGY_FLOW_INDICATOR_LENGTHS_MOBILE[2]) + ICON_WITH_LABEL_HEIGHT
  /**
   * When there are two levels, the height of the first level depends on the amount of the items of the first level
   */
  const firstLevelWithSecondLevelDesktopHeight =
    getEnergyFlowIndicatorHeight(FIRST_LEVEL_5_ITEM_ENERGY_FLOW_INDICATOR_LENGTHS[hasFiveItems ? 3 : 2]) + ICON_WITH_LABEL_HEIGHT
  const secondLevelHeight = getEnergyFlowIndicatorHeight(hasAllSecondLevelItems || !downMobile ? 3 : 0) + ICON_WITH_LABEL_HEIGHT

  const itemsWrapperHeight = liveViewHasSecondRow
    ? shallowLiveView
      ? firstLevelHeight + secondLevelHeight
      : firstLevelWithSecondLevelDesktopHeight + secondLevelHeight
    : firstLevelHeight

  const { classes, cx } = useStyles({ angle: maxAngle, itemsWrapperHeight })

  const labelVariant: Variant = shallowLiveView ? 'subtitle2' : 'h4'
  const valueVariant: Variant = shallowLiveView ? 'caption' : 'subtitle2'
  const hasSubItems = Boolean(consumerCount) || Boolean(wallboxCount)

  /* calculate the maximal angle of the liveView items related to the LiveView container width */
  useEffect(() => {
    const width = ref.current?.clientWidth ?? 120
    const widthFactor = shallowLiveView ? 1.24 : 0.45
    const cosValue = width ? (width / 2 - 20) / (width * widthFactor) : 0
    const acosValue = cosValue * (180 / Math.PI)
    const translationY = shallowLiveView ? width * 0.9 : clamp(width * 0.18, 100, 120)
    if (width) setTranslationY(translationY)
    setMaxAngle(acosValue)
  }, [ref.current?.clientWidth, shallowLiveView, hasSubItems])

  /* current mobile implementation expects 5 items with the first one empty */
  const itemsLength = shallowLiveView ? 5 : items.length

  /* whole angle of the liveView divided with the length of the items minus one */
  const itemAngle = calculateItemAngle(maxAngle, itemsLength)

  const StorageImage = isFlowTheme ? HagerStorage : Storage

  return (
    <Box className={classes.middleWrapper} ref={ref} data-qa={DASHBOARD_LIVE_VIEW_ID}>
      <Box className={classes.itemsWrapper}>
        {items.map((item, currentIndex) => {
          /* current mobile implementation expects 5 items with the first one empty */
          const index = shallowLiveView && items.length === FIRST_LEVEL_4_ITEMS ? currentIndex + FIRST_LEVEL_FIRST_ICON_REPLACEMENT : currentIndex
          const { children, ...restItemProps } = item
          const currentItemAngle = -maxAngle + itemAngle * index
          const side = getItemSide(currentIndex, itemsLength)

          return (
            <LiveViewItem
              labelVariant={labelVariant}
              valueVariant={valueVariant}
              key={`${item.label}_${index}`}
              rotateIcon={currentItemAngle}
              {...restItemProps}
              energyFlowIndicatorsCount={getEnergyFlowIndicatorAmountLiveItem(index, shallowLiveView, itemsLength)}
              translationY={translationY}
              side={side}
            >
              {children?.map((child, childIndex) => {
                const { children: childChildren, ...restChildProps } = child
                const itemAngle = calculateSubItemAngle({
                  subItemsLength: children.length,
                  index: childIndex,
                  downMd: shallowLiveView,
                  itemsLength,
                })

                const subItemKey = `${child.label}_${childIndex}`
                const side = getSubItemSide(childIndex, children.length)

                return (
                  <LiveViewSubItem
                    labelVariant={labelVariant}
                    valueVariant={valueVariant}
                    key={subItemKey}
                    {...restChildProps}
                    rotateIcon={itemAngle}
                    rotateItem={-itemAngle}
                    energyFlowIndicatorsCount={getEnergyFlowIndicatorAmountSubLiveItem(childIndex, shallowLiveView, downMd, children?.length ?? 0)}
                    side={side}
                    parentAngle={currentItemAngle}
                  />
                )
              })}
            </LiveViewItem>
          )
        })}
      </Box>
      {isFarm ? (
        <Box className={classes.farmProduction}>
          <Box className={cx(classes.lowerStorage, classes.leftStorage)}>
            <StorageImage />
          </Box>
          <Box className={classes.middleStorage}>
            <StorageImage />
          </Box>
          <Box className={cx(classes.lowerStorage, classes.rightStorage)}>
            <StorageImage />
          </Box>
        </Box>
      ) : (
        <Box className={classes.productIcon}>
          <StorageImage />
        </Box>
      )}
    </Box>
  )
}

export default LiveView

const getItemSide = (index: number, itemCount: number): LiveViewItemSide => {
  const isFirstItem = index === 0
  if (isFirstItem) return 'left'

  const isLastItem = index === itemCount - 1
  if (isLastItem) return 'right'

  return 'center'
}

const getSubItemSide = (index: number, subItemCount: number): LiveViewItemSide => {
  const middleIndex = (subItemCount - 1) / 2
  if (index < middleIndex) return 'left'
  if (index > middleIndex) return 'right'
  return 'center'
}
